import { useMemo, useState } from 'react';
import {
  usePlayback,
  useUptime,
  exportCsvUrl,
  enumerateDates,
  alignUptimeSeries,
  type AnalyticsRange,
  type AnalyticsScope,
} from '../../api/analytics.ts';
import { useScreens } from '../../api/screens.ts';
import { useGroups } from '../../api/groups.ts';
import { ErrorState, EmptyState } from '../../components/ui.tsx';
import { TargetPicker, type TargetOption } from '../schedules/TargetPicker.tsx';
import { Bar, Sparkline } from './charts.tsx';
import { formatDurationWords } from '../../lib/format.ts';

// --- Period -------------------------------------------------------------------
type Preset = 'today' | '7d' | '30d' | 'custom';
const PRESETS: Array<{ value: Preset; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'custom', label: 'Custom' },
];

/** Local-time YYYY-MM-DD (the API treats the range as local dates). */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return ymd(d);
}
function rangeFor(preset: Preset, from: string, to: string): AnalyticsRange {
  if (preset === 'today') return { from: ymd(new Date()), to: ymd(new Date()) };
  if (preset === '7d') return { from: daysAgo(6), to: ymd(new Date()) };
  if (preset === '30d') return { from: daysAgo(29), to: ymd(new Date()) };
  return { from: from || ymd(new Date()), to: to || ymd(new Date()) };
}

// --- Scope --------------------------------------------------------------------
type ScopeMode = 'org' | 'target';

function parseTarget(id: string | null): AnalyticsScope {
  if (!id) return { scope_type: 'org' };
  const [kind, ...rest] = id.split(':');
  const scopeId = rest.join(':');
  if ((kind === 'screen' || kind === 'group') && scopeId) return { scope_type: kind, scope_id: scopeId };
  return { scope_type: 'org' };
}

/** Empty-state glyph — a bar chart, same loose round-stroke family as the nav. */
function ChartGlyph() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 3v18h18" />
      <rect x="7" y="12" width="3" height="6" rx="0.8" />
      <rect x="12.5" y="8" width="3" height="10" rx="0.8" opacity="0.75" />
      <rect x="18" y="5" width="3" height="13" rx="0.8" opacity="0.5" />
    </svg>
  );
}

export function AnalyticsPage() {
  const [preset, setPreset] = useState<Preset>('7d');
  const [customFrom, setCustomFrom] = useState(daysAgo(6));
  const [customTo, setCustomTo] = useState(ymd(new Date()));
  const [scopeMode, setScopeMode] = useState<ScopeMode>('org');
  const [targetId, setTargetId] = useState<string | null>(null);

  const range = useMemo(() => rangeFor(preset, customFrom, customTo), [preset, customFrom, customTo]);
  const scope = useMemo<AnalyticsScope>(
    () => (scopeMode === 'org' ? { scope_type: 'org' } : parseTarget(targetId)),
    [scopeMode, targetId],
  );

  const screens = useScreens();
  const groups = useGroups();
  const playback = usePlayback(range, scope);
  const uptime = useUptime(range, scope);

  const targetOptions = useMemo<TargetOption[]>(() => {
    const g: TargetOption[] = (groups.data ?? []).map((x) => ({
      id: `group:${x.id}`,
      label: x.name,
      kind: 'group',
      sub: `${x.screen_ids.length} screen${x.screen_ids.length === 1 ? '' : 's'}`,
    }));
    const s: TargetOption[] = (screens.data ?? []).map((x) => ({
      id: `screen:${x.id}`,
      label: x.name,
      kind: 'screen',
      sub: x.location ?? undefined,
    }));
    return [...g, ...s];
  }, [groups.data, screens.data]);

  // Uptime series per screen, aligned to the full period so unreported dates
  // (the API's uptime series is sparse — not zero-filled) render as gaps, never 0%.
  const rangeDates = useMemo(() => enumerateDates(range), [range]);
  const seriesByScreen = useMemo(() => {
    const map = new Map<string, Array<number | null>>();
    for (const row of uptime.data?.perScreen ?? [])
      map.set(row.screenId, alignUptimeSeries(rangeDates, row.days));
    return map;
  }, [uptime.data, rangeDates]);

  const isLoading = playback.isLoading || uptime.isLoading;
  const totals = playback.data?.totals;
  const perScreen = playback.data?.perScreen ?? [];
  const perMedia = playback.data?.perMedia ?? [];
  const maxMediaPlays = Math.max(1, ...perMedia.map((m) => m.plays));
  const totalMediaPlays = Math.max(1, perMedia.reduce((a, m) => a + m.plays, 0));
  const hasData = (totals?.plays ?? 0) > 0 || perScreen.some((s) => s.plays > 0);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Analytics</h1>
          <div className="subtitle">Playback and uptime across your fleet.</div>
        </div>
      </div>

      {/* Filters */}
      <div className="toolbar a8-filters">
        <div className="seg" role="tablist" aria-label="Time period">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              role="tab"
              aria-selected={preset === p.value}
              className={preset === p.value ? 'active' : ''}
              onClick={() => setPreset(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="row a8-custom-range">
            <label className="sr-only" htmlFor="a8-from">From</label>
            <input id="a8-from" type="date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} />
            <span className="text-dim">to</span>
            <label className="sr-only" htmlFor="a8-to">To</label>
            <input id="a8-to" type="date" value={customTo} min={customFrom} onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        )}

        <div className="spacer" />

        <div className="seg" role="tablist" aria-label="Scope">
          <button role="tab" aria-selected={scopeMode === 'org'} className={scopeMode === 'org' ? 'active' : ''} onClick={() => setScopeMode('org')}>
            Whole org
          </button>
          <button role="tab" aria-selected={scopeMode === 'target'} className={scopeMode === 'target' ? 'active' : ''} onClick={() => setScopeMode('target')}>
            Screen or group
          </button>
        </div>
        {scopeMode === 'target' && (
          <TargetPicker options={targetOptions} value={targetId} onChange={setTargetId} />
        )}
      </div>

      {playback.isError || uptime.isError ? (
        <ErrorState
          message="Could not load analytics."
          onRetry={() => {
            void playback.refetch();
            void uptime.refetch();
          }}
        />
      ) : isLoading ? (
        <AnalyticsSkeleton />
      ) : !hasData ? (
        <div className="card card-pad">
          <EmptyState icon={<ChartGlyph />} title="No playback data yet">
            <p className="text-dim">
              Events appear once screens play content on the updated player. Check back after your
              screens have been running for a bit.
            </p>
          </EmptyState>
        </div>
      ) : (
        <>
          {/* Headline stats */}
          <div className="grid cols-3 mb-4">
            <div className="card stat">
              <span className="label">Total plays</span>
              <span className="value">{(totals?.plays ?? 0).toLocaleString()}</span>
            </div>
            <div className="card stat">
              <span className="label">Total playtime</span>
              <span className="value">{formatDurationWords(totals?.durationMs ?? 0)}</span>
            </div>
            <div className="card stat">
              <span className="label">Fleet uptime</span>
              <span className="value">
                {totals?.uptimePct == null ? <span className="text-dim">—</span> : `${totals.uptimePct}%`}
              </span>
            </div>
          </div>

          {/* Export */}
          <div className="row mb-4 a8-export">
            <span className="text-dim" style={{ fontSize: 'var(--fs-sm)' }}>Export</span>
            <a className="btn sm" href={exportCsvUrl(range, scope, 'playback')} download>
              Playback CSV
            </a>
            <a className="btn sm" href={exportCsvUrl(range, scope, 'uptime')} download>
              Uptime CSV
            </a>
          </div>

          <div className="grid" style={{ gap: 'var(--sp-4)' }}>
            {/* Per-screen */}
            <div className="card card-pad">
              <div className="card-title">By screen</div>
              <div className="table-wrap cards">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Screen</th>
                      <th>Plays</th>
                      <th>Playtime</th>
                      <th>Uptime</th>
                      <th>Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perScreen.map((s) => (
                      <tr key={s.screenId}>
                        <td className="cell-strong">{s.name}</td>
                        <td data-label="Plays">{s.plays.toLocaleString()}</td>
                        <td data-label="Playtime">{formatDurationWords(s.durationMs)}</td>
                        <td data-label="Uptime">
                          {s.uptimePct == null ? (
                            <span className="cell-dim">—</span>
                          ) : (
                            <div className="a8-uptime-cell">
                              <Bar pct={s.uptimePct} />
                              <span className="a8-uptime-val">{s.uptimePct}%</span>
                            </div>
                          )}
                        </td>
                        <td data-label="Trend">
                          <Sparkline points={seriesByScreen.get(s.screenId) ?? []} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top content */}
            <div className="card card-pad">
              <div className="card-title">Top content</div>
              {perMedia.length === 0 ? (
                <p className="text-dim">No content has played in this period.</p>
              ) : (
                <div className="table-wrap cards">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Content</th>
                        <th>Plays</th>
                        <th>Playtime</th>
                        <th>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perMedia.map((m) => (
                        <tr key={m.mediaId}>
                          <td className="cell-strong">
                            {m.name ?? <span className="text-dim">deleted media</span>}
                          </td>
                          <td data-label="Plays">{m.plays.toLocaleString()}</td>
                          <td data-label="Playtime">{formatDurationWords(m.durationMs)}</td>
                          <td data-label="Share">
                            <div className="a8-share-cell">
                              <Bar pct={(m.plays / maxMediaPlays) * 100} tone="neutral" />
                              <span className="a8-share-val">{Math.round((m.plays / totalMediaPlays) * 100)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Loading skeleton mirroring the stats + tables layout. */
function AnalyticsSkeleton() {
  return (
    <div aria-hidden>
      <div className="grid cols-3 mb-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="card stat">
            <span className="a8-skel a8-skel-line" style={{ width: 70 }} />
            <span className="a8-skel a8-skel-line lg" style={{ width: 110 }} />
          </div>
        ))}
      </div>
      <div className="card card-pad">
        <span className="a8-skel a8-skel-line" style={{ width: 120, marginBottom: 'var(--sp-3)' }} />
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="a8-skel a8-skel-row" />
        ))}
      </div>
    </div>
  );
}
